import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileTabsViewer } from '@localagent/ui';
import type { WorkspaceRoutingClass } from '@localagent/sdk';
import { useWorkspaceFileBrowser } from '../../hooks/useWorkspaceFileBrowser';
import { useWorkspaceFilesChanged } from '../../hooks/useWorkspaceFilesChanged';
import { useCloudFilesChanged } from '../../hooks/useCloudFilesChanged';
import { useEditorAutosavePreference } from '../../hooks/useUserPreferences';
import { WorkspaceDirectoryTree } from './WorkspaceDirectoryTree';
import { FileWorkItemTrail } from '../work/FileWorkItemTrail';

/**
 * The chat view's file surface WITHOUT the chat:
 * the same `useWorkspaceFileBrowser` + `WorkspaceDirectoryTree` +
 * `FileTabsViewer` composition `ChatWorkspaceLayout` mounts, for a standalone
 * host (the workspace detail page's Files tab). Same editing model (dirty
 * markers, CAS saves, autosave preference, conflict flow), same live-refresh
 * triggers (local watcher + cloud push), same not-ready affordances — because
 * it is the same code.
 *
 * Deliberately NOT here: chat framing (`chat`/`header` slots, panel
 * position/resize prefs), mention drag sources, and the turn-end refresh
 * handle — those are chat couplings with no meaning on a standalone page.
 */
export function WorkspaceFilesPanel({
  workspaceId,
  workspacePath,
  mode,
}: {
  workspaceId: string;
  /** Filesystem root of a LOCAL workspace — the daemon's `workspaceRoot`. */
  workspacePath?: string;
  /** Workspace transport; `undefined` while the workspace load resolves. */
  mode?: WorkspaceRoutingClass;
}) {
  const { autosaveEnabled } = useEditorAutosavePreference();
  const fb = useWorkspaceFileBrowser({ workspaceId, workspacePath, mode, autosaveEnabled });
  const { hasDirty, lastOwnSaveAtRef, recheckActiveFile, refreshTree } = fb;

  // `?path=` opens one file directly, so a link from elsewhere in the app — the
  // route a refused comparison offers — lands on the file itself
  // rather than on an empty viewer. Honoured once per requested path.
  const [searchParams] = useSearchParams();
  const requestedPath = searchParams.get('path');
  const openedRequestRef = useRef<string | null>(null);
  const { openFile } = fb;
  useEffect(() => {
    if (!requestedPath || openedRequestRef.current === requestedPath) return;
    openedRequestRef.current = requestedPath;
    openFile(requestedPath);
  }, [requestedPath, openFile]);

  // Exit guard (same as ChatWorkspaceLayout): armed ONLY while a tab is
  // dirty. On web this is the native leave-page prompt; on desktop Electron
  // routes the blocked unload through the main process's `will-prevent-unload`
  // handler.
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requires returnValue to be set for the prompt to appear.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasDirty]);

  // Local watcher trigger: tree refresh + the active-tab fast path,
  // exactly as the chat layout wires it. Enabled only for local workspaces.
  const onWatcherFilesChanged = useCallback(() => {
    void recheckActiveFile();
    void refreshTree();
  }, [recheckActiveFile, refreshTree]);
  useWorkspaceFilesChanged({
    workspaceId,
    enabled: mode === 'local',
    onFilesChanged: onWatcherFilesChanged,
  });

  // Cloud push trigger: refresh on workspace.filesChanged frames, with
  // the hook's debounce + self-echo suppression via lastOwnSaveAtRef.
  const onCloudFilesChanged = useCallback(() => {
    void refreshTree();
  }, [refreshTree]);
  useCloudFilesChanged({
    workspaceId,
    enabled: mode === 'cloud',
    lastOwnSaveAtRef,
    onFilesChanged: onCloudFilesChanged,
  });

  // Full-height flex: fixed-width tree column, viewer takes the rest.
  return (
    <div className="flex h-full min-h-0 min-w-0" data-testid="workspace-files-panel">
      <div className="w-56 flex-shrink-0 border-r border-border">
        <WorkspaceDirectoryTree
          rootEntries={fb.rootEntries}
          loadChildren={fb.loadChildren}
          notReady={fb.notReady}
          resume={fb.resume}
          activePath={fb.activePath}
          onOpenFile={fb.openFile}
          onRefresh={() => void fb.refreshTree({ manual: true })}
          refreshing={fb.refreshing}
          refreshError={fb.refreshError}
          refreshRef={fb.treeRefreshRef}
        />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <FileWorkItemTrail workspaceId={workspaceId} path={fb.activePath} />
        <div className="min-h-0 flex-1">
          <FileTabsViewer
            openPaths={fb.openPaths}
            activePath={fb.activePath}
            onActivate={fb.setActive}
            onClose={fb.closeFile}
            read={fb.read}
            activeSizeBytes={fb.activeSize}
            onOpenExternally={fb.openExternal}
            editing={fb.editing}
          />
        </div>
      </div>
    </div>
  );
}
